// eRecords look for GoAlert, taken from concierge-ui / infort-ui: warm paper
// and charcoal (#333132). The eRecords red is kept for the logo and for real
// signals (critical, unacknowledged, errors); everything else is neutral.
import { Theme } from '@mui/material'
import { SystemStyleObject } from '@mui/system'
import { PaletteOptions } from '@mui/material/styles/createPalette'

// BRAND_SOURCE is the default theme source color. While a user keeps it, the
// palette below replaces the generated Material You one; picking another color
// in the theme picker falls back to upstream behavior.
export const BRAND_SOURCE = '#c1230b'

export const brandColors = {
  light: {
    bg: '#f8f4ee',
    surface: '#fffdf9',
    line: '#e8dfd3',
    ink: '#333132',
    muted: '#6e655c',
    brand: '#c1230b',
    accentSoft: '#fbe8e4',
    hover: '#f3ece3',
    soft: '#efe8df',
    ok: '#2f7d4f',
    warn: '#a35a00',
  },
  dark: {
    bg: '#1a1819',
    surface: '#262324',
    line: '#3a3536',
    ink: '#efe9e3',
    muted: '#aca299',
    brand: '#ec6557',
    accentSoft: '#3a2320',
    hover: '#2e2a2b',
    soft: '#332f30',
    ok: '#6fcf97',
    warn: '#f5c77e',
  },
}

// isBrandTheme reports whether the eRecords palette is active (its app bar is
// light, so toolbar text must not be the white upstream uses on primary).
export function isBrandTheme(theme: {
  palette: { background: { default: string } }
}): boolean {
  return (
    theme.palette.background.default === brandColors.light.bg ||
    theme.palette.background.default === brandColors.dark.bg
  )
}

export const sans =
  "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
export const serif = "'Fraunces Variable', Georgia, 'Times New Roman', serif"

export function brandPalette(mode: 'light' | 'dark'): PaletteOptions {
  const c = brandColors[mode]
  return {
    mode,
    // neutral primary: buttons, tabs, selection are charcoal (light) / paper (dark)
    primary:
      mode === 'dark'
        ? { main: '#e9e2da', contrastText: '#1a1819' }
        : { main: '#333132', contrastText: '#fffdf9' },
    secondary: { main: mode === 'dark' ? '#aca299' : '#8a7f74' },
    background: { default: c.bg, paper: c.surface },
    text: { primary: c.ink, secondary: c.muted },
    divider: c.line,
    error: { main: mode === 'dark' ? '#f0857a' : '#b42318' },
    warning: { main: mode === 'dark' ? '#f5c77e' : '#a35a00' },
    success: { main: c.ok },
  }
}

// personColor gives each person a stable soft color (calendar shifts).
const people = {
  light: [
    ['#e3ecf8', '#4f74a8'],
    ['#e4f1e7', '#3f7d50'],
    ['#f6eadb', '#a8702f'],
    ['#eee4f6', '#7c5a9c'],
    ['#dff0ef', '#337f7c'],
    ['#f7e4e7', '#a3505f'],
    ['#ecebe1', '#6f6a4f'],
  ],
  dark: [
    ['#263449', '#8fb1e0'],
    ['#233a2c', '#86c79a'],
    ['#3d3022', '#e0b47c'],
    ['#33293f', '#c1a3e0'],
    ['#1f3938', '#7fcdc8'],
    ['#40262c', '#e6a0ad'],
    ['#33322a', '#c9c3a0'],
  ],
}

export function personColor(
  id: string | undefined,
  mode: 'light' | 'dark',
): { bg: string; line: string } {
  let h = 0
  for (const ch of id ?? '') h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const [bg, line] = people[mode][h % people[mode].length]
  return { bg, line }
}

// crumbSx styles the app bar's breadcrumbs (main/components/BreadCrumb.tsx)
// on the eRecords theme: small and muted, the current page in ink.
export function crumbSx(
  theme: Theme,
  current: boolean,
): SystemStyleObject<Theme> {
  const c = brandColors[theme.palette.mode]
  return {
    px: 0.75,
    py: 0.25,
    borderRadius: '8px',
    fontSize: 15,
    fontWeight: current ? 600 : 500,
    color: current ? c.ink : c.muted,
  }
}

export function crumbLinkSx(theme: Theme): SystemStyleObject<Theme> {
  const c = brandColors[theme.palette.mode]
  return {
    textDecoration: 'none',
    '&:hover': { textDecoration: 'none' },
    '&:hover > h6': { bgcolor: c.hover, color: c.ink },
  }
}
